import type { ClinicianTypeInput } from '@/lib/types';
import type { TranslationKey } from '@heidi/runtime/i18n';

function specialtyHaystack(specialty: ClinicianTypeInput | null): string {
  if (!specialty) return '';
  return [
    specialty.clinical_content_group,
    specialty.group?.name,
    specialty.group?.id,
    specialty.name,
    ...(specialty.groups?.map((g) => g.name) ?? []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

/**
 * Maps clinician specialty metadata to a signup template-editor placeholder string key.
 * Order of checks matters (more specific / overlapping terms first).
 */
export function getSignupTemplateDescriptionPlaceholderKey(
  specialty: ClinicianTypeInput | null
): TranslationKey {
  if (!specialty) {
    return 'signup.templateDescriptionPlaceholderDefault';
  }

  const h = specialtyHaystack(specialty);
  const t = (re: RegExp) => re.test(h);

  if (t(/\b(emergency|e\.?d\.?|\bed\b|a&e|acute care|trauma|paramedic|urgent care)\b/)) {
    return 'signup.templateDescriptionPlaceholderEmergency';
  }

  if (
    t(
      /psychiatr|psycholog|mental health|\bcmh\b|\bcbt\b|counsell|counselor|psychotherap|addiction medicine|addiction psych|wellbeing practitioner/
    )
  ) {
    return 'signup.templateDescriptionPlaceholderMentalHealth';
  }

  if (t(/dent|orthodont|oral surgeon|periodont|endodont|prosthodont/)) {
    return 'signup.templateDescriptionPlaceholderDental';
  }

  if (
    t(
      /physio|physical therap|osteopath|chiropract|sports medicine|musculoskeletal|\bmsk\b|exercise physiolog/
    )
  ) {
    return 'signup.templateDescriptionPlaceholderPhysiotherapy';
  }

  if (
    t(
      /\b(gp|general pract|family pract|family medicine|primary care|general medicine)\b/
    ) ||
    t(/general practitioner/)
  ) {
    return 'signup.templateDescriptionPlaceholderPrimaryCare';
  }

  if (t(/paediatric|pediatric|neonat|child health/)) {
    return 'signup.templateDescriptionPlaceholderPaediatric';
  }

  if (
    t(
      /\bsurg|surgeon\b|orthopaedic surgery|orthopedic surgery|operating room|theatre|perioperative|anaesth|anesthes/
    )
  ) {
    return 'signup.templateDescriptionPlaceholderSurgical';
  }

  if (
    t(
      /\bnurs|midwi|enrolled nurse|\bnp\b|nurse practitioner|clinical nurse|rn\b|registered nurse/
    )
  ) {
    return 'signup.templateDescriptionPlaceholderNursing';
  }

  if (
    t(
      /occupational therap|speech path|diet|nutrition|radiolog|sonograph|patholog|\blaboratory\b|\blab\b|allied health|podiatr|optomet|ophthalm|audiolog/
    )
  ) {
    return 'signup.templateDescriptionPlaceholderAlliedHealth';
  }

  if (
    t(
      /consultant|cardiolog|neurolog|oncolog|dermatolog|gastroenterolog|rheumatolog|endocrinolog|urolog|nephrolog|pulmonolog|haematolog|hematolog|immunolog|geriatr|specialist physician|internal medicine|hospitalist/
    )
  ) {
    return 'signup.templateDescriptionPlaceholderSpecialist';
  }

  return 'signup.templateDescriptionPlaceholderDefault';
}
